(() => {
  'use strict';
  if (customElements.get('cm-navigation')) return;
  const unique = values => [...new Set(values.flat().filter(value => typeof value === 'string' && value.trim()))];
  const previewUrl = path => {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) return url;
    const theme = window.Shopify?.theme;
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    const id = theme && theme.role !== 'main' ? theme.id : preview;
    if (id) url.searchParams.set('preview_theme_id', id);
    return url;
  };
  const groupsFor = (kind, data) => {
    const options = data.items.flatMap(item => item.options || []);
    const valuesFor = pattern => unique(options.filter(option => pattern.test(option.name)).flatMap(option => option.values));
    const devices = unique(data.items.flatMap(item => item.model || []));
    if (['color', 'links'].includes(kind)) return [];
    if (kind === 'watch') return [{ title: 'Saat ölçüsü', param: 'cm_size', values: valuesFor(/model|ölçü|size/i) }];
    return [{ title: 'Model', param: 'cm_device', values: ['airpods','tablet','laptop'].includes(kind) ? devices : valuesFor(/model|ölçü|size/i) }];
  };
  const brandFor = (label, fallback = 'Diğer') => {
    if (/^(iphone|apple)/i.test(label)) return 'Apple iPhone';
    if (/^(samsung|galaxy)/i.test(label)) return 'Samsung Galaxy';
    if (/^(xiaomi|redmi|poco|mi\b|\d+T\b)/i.test(label)) return 'Xiaomi';
    for (const brand of ['Oppo', 'Huawei', 'Vivo', 'Tecno', 'Realme', 'Honor', 'Motorola']) {
      if (label.toLowerCase().startsWith(brand.toLowerCase())) return brand;
    }
    if (/^one\s?plus/i.test(label)) return 'OnePlus';
    if (/^[iİ]nfinix/i.test(label)) return 'Infinix';
    return { Apple: 'Apple iPhone', Samsung: 'Samsung Galaxy' }[fallback] || fallback;
  };
  const phoneModels = () => [...document.querySelectorAll('#cm-model-dialog [data-cm-model-value]')].map(button => ({
    label: button.dataset.cmModelValue, param: button.dataset.cmModelParam,
    tag: button.dataset.cmModelTag, collection: button.dataset.cmModelCollection, brand: button.dataset.cmBrand
  })).filter(model => model.label && model.param);
  const selectionUrl = (path, param, value) => {
    const url = previewUrl(path);
    url.searchParams.delete('page');
    if (param.startsWith('filter.p.m.custom.')) {
      [...url.searchParams.keys()].filter(key => /^filter\.p\.m\.custom\..*model/.test(key)).forEach(key => url.searchParams.delete(key));
    }
    url.searchParams.set(param, value);
    return url;
  };
  class CasemetaNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.closest('dialog');
      this.scroller = this.querySelector('[data-menu-scroll]');
      this.views = new Map([...this.querySelectorAll('[data-menu-view]')].map(view => [view.dataset.menuView, view]));
      this.current = 'root';
      this.positions = new Map();
      this.openers = new Map();
      this.requests = new Map();
      this.preparedImages = new WeakSet();
      // Warm only the first menu images after the page has loaded. Hidden lazy
      // images otherwise start downloading together only when the menu opens.
      const prepareOnIntent = () => this.prepareImages(true);
      document.querySelectorAll(`[data-cm-open="${this.dialog.id}"]`).forEach(button => {
        ['pointerenter', 'pointerdown', 'focusin'].forEach(type => button.addEventListener(type, prepareOnIntent, { signal, passive: true }));
      });
      const scheduleImages = () => {
        if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
        this.imageTimer = setTimeout(() => {
          if ('requestIdleCallback' in window) this.imageIdle = requestIdleCallback(() => this.prepareImages(), { timeout: 1000 });
          else this.prepareImages();
        }, 150);
      };
      if (document.readyState === 'complete') scheduleImages();
      else window.addEventListener('load', scheduleImages, { once: true, signal });
      this.querySelectorAll('a[href]').forEach(link => { link.href = previewUrl(link.getAttribute('href')).href; });
      const preview = previewUrl(location.href).searchParams.get('preview_theme_id');
      if (preview) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = 'preview_theme_id'; hidden.value = preview;
        this.querySelector('.cm-nav-search').append(hidden);
      }
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-menu-go]');
        if (button) {
          event.preventDefault();
          this.navigate(button.dataset.menuGo, button);
        }
        const retry = event.target.closest('[data-menu-retry]');
        if (retry) this.loadOptions(retry.closest('[data-menu-view]'));
        const modelLink = event.target.closest('[data-menu-phone]');
        if (modelLink && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          const model = phoneModels().find(item => item.label === modelLink.dataset.menuPhone);
          if (model) window.CasemetaStorefront?.saveModel(model);
        }
      }, { signal });
      this.dialog.addEventListener('close', () => this.reset(), { signal });
      // A closed dialog has no layout, so its scroll position must be restored
      // after showModal makes the scroll container measurable again.
      this.openObserver = new MutationObserver(() => {
        if (this.dialog.open) {
          this.prepareImages(true);
          this.scroller.scrollTop = this.positions.get(this.current) || 0;
        }
      });
      this.openObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      this.querySelectorAll('[data-menu-rail]').forEach(rail => this.enableDrag(rail, signal));
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        if (!this.dialog.open) {
          this.dialog.cmOpener = document.activeElement;
          this.dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
        }
        const category = event.target.closest('[data-menu-go]');
        this.navigate(category?.dataset.menuGo || 'root');
        if (!category) event.target.scrollIntoView({ block: 'nearest', inline: 'center' });
      }, { signal });
    }
    disconnectedCallback() {
      clearTimeout(this.imageTimer);
      if (this.imageIdle !== undefined) cancelIdleCallback(this.imageIdle);
      this.openObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
    prepareImages(intent = false) {
      if (!this.isConnected) return;
      const root = this.views.get('root');
      if (!root) return;
      const images = [
        ...[...root.querySelectorAll('[data-menu-rail] img')].slice(0, 4),
        ...[...root.querySelectorAll('.cm-nav-product__image img')].slice(0, 8)
      ];
      images.forEach(image => {
        image.fetchPriority = intent ? 'auto' : 'low';
        if (this.preparedImages.has(image)) return;
        this.preparedImages.add(image);
        image.loading = 'eager';
        // Decode offscreen without delaying opening, links, or keyboard focus.
        image.decode?.().catch(() => {});
      });
    }
    reset() {
      this.current = 'root';
      this.positions.clear();
      this.openers.clear();
      this.views.forEach((view, id) => { view.hidden = id !== 'root'; });
      this.scroller.scrollTop = 0;
    }
    navigate(id, button) {
      const next = this.views.get(id);
      if (!next) return;
      const previous = this.current;
      this.positions.set(previous, this.scroller.scrollTop);
      if (button && !button.hasAttribute('data-menu-back')) this.openers.set(id, button);
      this.views.forEach((view, key) => { view.hidden = key !== id; });
      this.current = id;
      this.scroller.scrollTop = this.positions.get(id) || 0;
      const focus = button?.hasAttribute('data-menu-back') ? this.openers.get(previous) : next.querySelector('.cm-nav-title');
      focus?.focus({ preventScroll: true });
      this.loadOptions(next);
    }
    enableDrag(rail, signal) {
      let drag;
      let suppressClickUntil = 0;
      rail.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        drag = { id: event.pointerId, x: event.clientX, left: rail.scrollLeft, moved: false };
      }, { signal });
      rail.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) > 6) {
          drag.moved = true;
          rail.setPointerCapture(event.pointerId);
          rail.classList.add('is-dragging');
        }
        if (drag.moved) {
          event.preventDefault();
          rail.scrollLeft = drag.left - distance;
        }
      }, { signal });
      const finish = event => {
        if (!drag || drag.id !== event.pointerId) return;
        if (drag.moved) suppressClickUntil = performance.now() + 300;
        drag = null;
        rail.classList.remove('is-dragging');
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
      };
      ['pointerup','pointercancel','lostpointercapture'].forEach(type => rail.addEventListener(type, finish, { signal }));
      rail.addEventListener('pointerleave', event => { if (drag && !drag.moved) finish(event); }, { signal });
      rail.addEventListener('dragstart', event => event.preventDefault(), { signal });
      rail.addEventListener('click', event => {
        if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
      }, { capture: true, signal });
      rail.addEventListener('keydown', event => {
        if (!['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const distance = (rail.firstElementChild?.getBoundingClientRect().width || 120) + 12;
        rail.scrollBy({ left: event.key === 'ArrowRight' ? distance : -distance, behavior: 'auto' });
      }, { signal });
    }
    async fetchOptions(path) {
      const data = { tags: [], items: [] };
      const visited = new Set();
      let next = path;
      while (next) {
        const url = previewUrl(next);
        if (url.origin !== location.origin) throw new Error('Invalid collection origin');
        url.searchParams.set('section_id', 'cm-menu-options');
        if (visited.has(url.href)) throw new Error('Repeated menu data page');
        visited.add(url.href);
        const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
        if (!response.ok) throw new Error('Menu options unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const script = html.querySelector('[data-cm-menu-data]');
        if (!script) throw new Error('Menu data missing');
        const page = JSON.parse(script.textContent);
        data.tags.push(...(page.tags || []));
        data.items.push(...(page.items || []));
        next = page.next;
      }
      return data;
    }
    optionLink(view, param, value, phone = false) {
      const link = document.createElement('a'); link.className = 'cm-nav-link';
      link.href = selectionUrl(view.dataset.menuTarget, param, value).href;
      if (phone) link.dataset.menuPhone = value;
      const text = document.createElement('span');
      text.textContent = param === 'cm_size' ? value.replace(/^Apple Watch /, '') : value;
      const arrow = document.createElement('span'); arrow.className = 'cm-nav-chevron'; arrow.setAttribute('aria-hidden', 'true');
      link.append(text, arrow);
      return link;
    }
    brandViews(view, models, phone = false) {
      const brands = new Map();
      models.forEach(model => {
        const brand = brandFor(model.label, model.brand);
        if (!brands.has(brand)) brands.set(brand, []);
        if (!brands.get(brand).some(item => item.label === model.label)) brands.get(brand).push(model);
      });
      const content = document.createDocumentFragment();
      brands.forEach((items, brand) => {
        const id = `${view.dataset.menuView}-brand-${brands.size}-${[...brands.keys()].indexOf(brand)}`;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'cm-nav-link';
        button.dataset.menuGo = id;
        const label = document.createElement('span'); label.textContent = brand;
        const arrow = document.createElement('span'); arrow.className = 'cm-nav-chevron'; arrow.setAttribute('aria-hidden', 'true');
        button.append(label, arrow); content.append(button);
        const child = document.createElement('section'); child.className = 'cm-nav-view'; child.dataset.menuView = id; child.hidden = true;
        const back = document.createElement('button'); back.type = 'button'; back.className = 'cm-nav-back';
        back.dataset.menuGo = view.dataset.menuView; back.dataset.menuBack = '';
        const backArrow = document.createElement('span'); backArrow.textContent = '←'; backArrow.setAttribute('aria-hidden', 'true');
        back.append(backArrow, document.createTextNode(view.querySelector('.cm-nav-title').textContent));
        const heading = document.createElement('h2'); heading.className = 'cm-nav-title'; heading.tabIndex = -1; heading.textContent = brand;
        child.append(back, heading);
        items.forEach(model => child.append(this.optionLink(view, model.param, model.label, phone)));
        this.scroller.append(child); this.views.set(id, child);
      });
      return content;
    }
    async loadOptions(view) {
      const container = view.querySelector('[data-menu-options]');
      if (!container || container.dataset.state === 'loaded' || container.dataset.state === 'loading') return;
      container.dataset.state = 'loading';
      container.replaceChildren();
      const status = document.createElement('p');
      status.className = 'cm-nav-status'; status.textContent = 'Seçenekler yükleniyor…';
      container.append(status);
      const path = view.dataset.menuCollection;
      try {
        if (view.dataset.menuKind === 'cases') {
          const models = phoneModels();
          if (!models.length) throw new Error('Phone models unavailable');
          container.replaceChildren(this.brandViews(view, models, true));
          container.dataset.state = 'loaded';
          return;
        }
        if (!this.requests.has(path)) this.requests.set(path, this.fetchOptions(path));
        const data = await this.requests.get(path);
        if (!this.isConnected) return;
        const content = document.createDocumentFragment();
        groupsFor(view.dataset.menuKind, data).forEach(group => {
          if (!group.values.length) return;
          if (view.dataset.menuKind === 'screen') {
            content.append(this.brandViews(view, group.values.map(label => ({ label, param: group.param }))));
            return;
          }
          const heading = document.createElement('h3'); heading.textContent = group.title; content.append(heading);
          group.values.forEach(value => content.append(this.optionLink(view, group.param, value)));
        });
        if (!content.childNodes.length) throw new Error('No device models available');
        container.replaceChildren(content);
        container.dataset.state = 'loaded';
      } catch (error) {
        this.requests.delete(path);
        container.dataset.state = 'error';
        if (error.name === 'AbortError' || !this.isConnected) return;
        status.textContent = 'Seçenekler yüklenemedi. ';
        const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'cm-nav-retry';
        retry.dataset.menuRetry = ''; retry.textContent = 'Tekrar dene';
        status.append(retry);
      }
    }
  }
  customElements.define('cm-navigation', CasemetaNavigation);
})();
